import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'

import { createTheme, darken, ThemeProvider } from '@mui/material/styles'
import { correttoTheme } from 'styles/theme-corretto'
import { brand, fontFamily as correttoFontCss, semantic } from 'styles/tokens'

import Constants from 'common/constants'
import useGtmInjector from 'common/hooks/use-gtm-injector'
import AgencyService, { Queries } from 'common/services/agency'
import {
  AgencyServiceInterface,
  PropertyDisplaySettings
} from 'common/services/agency/interfaces'

import { useAgencyFavicon } from './favicon'

export function getDisplaySetting(
  settings: PropertyDisplaySettings | undefined | null,
  key: keyof PropertyDisplaySettings
): boolean {
  if (!settings) return true
  return settings[key] !== false
}

// Estilos do site com que a imobiliaria comeca (tambem o ponto de partida da
// tela Personalizar).
const STYLE_DEFAULTS = {
  cardStyle: 'CLASSIC',
  headerStyle: 'WHITE',
  footerStyle: 'CENTERED',
  heroStyle: 'CLASSIC',
  heroOverlayColor: '#000000',
  heroOverlayOpacity: 0.3
}

/**
 * Padrao do que o cliente da imobiliaria ve (site publico e assinatura)
 * quando ela nao personalizou nada: o da Corretto. As imobiliarias que ja
 * existiam tiveram o padrao antigo (vermelho e Roboto) gravado no cadastro pela
 * V480 do backend, entao o site delas nao muda.
 */
export const SITE_DEFAULTS = {
  primary: semantic.primary,
  secondary: brand.grafite as string,
  button: semantic.primary,
  fontFamily: 'Manrope',
  ...STYLE_DEFAULTS
}

/**
 * Padrao do portal do cliente: a imobiliaria personaliza, e o que ela deixar
 * em branco fica com a Corretto.
 */
export const ADMIN_DEFAULTS = {
  primary: semantic.primary,
  secondary: semantic.secondary,
  button: semantic.primary,
  fontFamily: 'Manrope',
  ...STYLE_DEFAULTS
}

export const COLOR_PRESETS = [
  {
    name: 'Corretto',
    primary: brand.azul,
    secondary: brand.grafite,
    button: brand.verdeEscuro
  },
  {
    name: 'Azul Corporativo',
    primary: '#1565C0',
    secondary: '#0D47A1',
    button: '#1565C0'
  },
  {
    name: 'Verde Moderno',
    primary: '#2E7D32',
    secondary: '#1B5E20',
    button: '#2E7D32'
  },
  {
    name: 'Laranja Energia',
    primary: '#E65100',
    secondary: '#BF360C',
    button: '#E65100'
  },
  {
    name: 'Roxo Criativo',
    primary: '#6A1B9A',
    secondary: '#4A148C',
    button: '#6A1B9A'
  },
  {
    name: 'Cinza Neutro',
    primary: '#455A64',
    secondary: '#263238',
    button: '#455A64'
  },
  {
    name: 'Vermelho Clássico',
    primary: '#C62828', // check-brand-ignore: opcao de cor da imobiliaria
    secondary: '#B71C1C', // check-brand-ignore
    button: '#C62828' // check-brand-ignore
  },
  {
    name: 'Teal Refinado',
    primary: '#00695C',
    secondary: '#004D40',
    button: '#00695C'
  },
  {
    name: 'Dourado Premium',
    primary: '#F9A825',
    secondary: '#F57F17',
    button: '#F9A825'
  },
  {
    name: 'Rosa Moderno',
    primary: '#AD1457',
    secondary: '#880E4F',
    button: '#AD1457'
  },
  {
    name: 'Marrom Terra',
    primary: '#5D4037',
    secondary: '#3E2723',
    button: '#5D4037'
  }
]

export const CARD_STYLE_OPTIONS = [
  { value: 'CLASSIC', label: 'Clássico' },
  { value: 'HORIZONTAL', label: 'Horizontal' },
  { value: 'MINIMAL', label: 'Minimalista' },
  { value: 'BOLD', label: 'Destaque' },
  { value: 'COMPACT', label: 'Compacto' }
]

export const HEADER_STYLE_OPTIONS = [
  { value: 'WHITE', label: 'Branco' },
  { value: 'PRIMARY', label: 'Cor primária' },
  { value: 'TRANSPARENT', label: 'Transparente' },
  { value: 'DARK', label: 'Escuro' }
]

export const FOOTER_STYLE_OPTIONS = [
  { value: 'CENTERED', label: 'Centralizado' },
  { value: 'COLUMNS', label: 'Colunas' },
  { value: 'MINIMAL', label: 'Minimalista' }
]

export const HERO_STYLE_OPTIONS = [
  { value: 'CLASSIC', label: 'Clássico' },
  { value: 'SPLIT', label: 'Dividido' },
  { value: 'DARK', label: 'Escuro' },
  { value: 'GRADIENT', label: 'Gradiente' },
  { value: 'MINIMAL', label: 'Minimalista' }
]

export const FONT_OPTIONS = [
  { value: 'Manrope', label: 'Manrope (padrão)' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Merriweather', label: 'Merriweather' }
]

// Manrope (fonte da Corretto e padrao dos sites) ja vem no index.html; as
// outras sao carregadas sob demanda.
const FONT_GOOGLE_SLUGS: Record<string, string> = {
  Roboto: 'Roboto:wght@300;400;500;700',
  Inter: 'Inter:wght@400;600;700',
  'Open Sans': 'Open+Sans:wght@400;600;700',
  Montserrat: 'Montserrat:wght@400;600;700',
  Lato: 'Lato:wght@400;700',
  Poppins: 'Poppins:wght@400;600;700',
  Raleway: 'Raleway:wght@400;600;700',
  'Playfair Display': 'Playfair+Display:wght@400;700',
  Merriweather: 'Merriweather:wght@400;700'
}

export const FONT_CSS: Record<string, string> = {
  Roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
  Manrope: '"Manrope", "Helvetica", "Arial", sans-serif',
  Inter: '"Inter", sans-serif',
  'Open Sans': '"Open Sans", sans-serif',
  Montserrat: '"Montserrat", sans-serif',
  Lato: '"Lato", sans-serif',
  Poppins: '"Poppins", sans-serif',
  Raleway: '"Raleway", sans-serif',
  'Playfair Display': '"Playfair Display", serif',
  Merriweather: '"Merriweather", serif'
}

export const DEFAULT_FONT_CSS = FONT_CSS.Manrope

/**
 * Traduz o nome da fonte escolhida pela imobiliaria para o valor CSS completo.
 * Fonte desconhecida (ou vazia) cai no padrao Manrope, nunca em `undefined` —
 * assim nenhum componente precisa repetir o fallback.
 */
export function resolveFontCss(family: string | null | undefined): string {
  if (!family) return DEFAULT_FONT_CSS
  return FONT_CSS[family] ?? DEFAULT_FONT_CSS
}

export function loadGoogleFont(family: string) {
  if (!family || family === 'Manrope') return
  const slug = FONT_GOOGLE_SLUGS[family]
  if (!slug) return
  const id = `gfont-global-${family.replace(/\s+/g, '-')}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${slug}&display=swap`
  document.head.appendChild(link)
}

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/

interface AgencyThemeContextInterface {
  agency: AgencyServiceInterface | null
  isLoading: boolean
  buttonColor: string
  cardStyle: string
  headerStyle: string
  footerStyle: string
  fontFamily: string
  fontCss: string
  heroStyle: string
  heroImageUrl: string | null
  heroOverlayColor: string
  heroOverlayOpacity: number
}

/**
 * - `admin`: backoffice e captador. Sempre com a cara da Corretto (decisao D1
 *   do plano CORRETTO); as cores da imobiliaria so chegam pelo contexto, para
 *   telas como Personalizar mostrarem o site dela.
 * - `portal`: portal do cliente da imobiliaria, com as cores dela.
 * - `site`: site publico da imobiliaria.
 */
export type AgencyThemeVariant = 'admin' | 'portal' | 'site'

interface AgencyThemeProviderProps {
  children: ReactNode
  variant: AgencyThemeVariant
  /**
   * Mantem o titulo da aba com o nome da Corretto (telas de acesso no host da
   * plataforma). Sem ele, `site` e `portal` usam o nome da imobiliaria.
   */
  platformTitle?: boolean
}

const AgencyThemeContext = createContext<AgencyThemeContextInterface>({
  agency: null,
  isLoading: false,
  buttonColor: SITE_DEFAULTS.button,
  cardStyle: 'CLASSIC',
  headerStyle: 'WHITE',
  footerStyle: 'CENTERED',
  fontFamily: 'Manrope',
  fontCss: DEFAULT_FONT_CSS,
  heroStyle: 'CLASSIC',
  heroImageUrl: null,
  heroOverlayColor: '#000000',
  heroOverlayOpacity: 0.3
})

export function AgencyThemeProvider({
  children,
  variant,
  platformTitle = false
}: AgencyThemeProviderProps) {
  const { pathname } = useLocation()
  // O contexto descreve o que o cliente da imobiliaria ve; no backoffice isso
  // e o site, entao `admin` resolve com os padroes do site.
  const defaults = variant === 'portal' ? ADMIN_DEFAULTS : SITE_DEFAULTS

  const { data: agency, isLoading } = useQuery(
    Queries.detail('default'),
    () => AgencyService.getDefaultAgency(),
    { staleTime: 5 * 60 * 1000, retry: false }
  )

  const {
    theme,
    buttonColorResolved,
    cardStyleResolved,
    headerStyleResolved,
    footerStyleResolved,
    fontFamilyResolved,
    fontCssResolved,
    heroStyleResolved,
    heroImageUrlResolved,
    heroOverlayColorResolved,
    heroOverlayOpacityResolved
  } = useMemo(() => {
    const primaryMain =
      agency?.primaryColor && HEX_REGEX.test(agency.primaryColor)
        ? agency.primaryColor
        : defaults.primary

    const secondaryMain =
      agency?.secondaryColor && HEX_REGEX.test(agency.secondaryColor)
        ? agency.secondaryColor
        : defaults.secondary

    const buttonMain =
      agency?.buttonColor && HEX_REGEX.test(agency.buttonColor)
        ? agency.buttonColor
        : defaults.button

    const cardStyle = agency?.cardStyle ?? 'CLASSIC'
    const headerStyle = agency?.headerStyle ?? 'WHITE'
    const footerStyle = agency?.footerStyle ?? 'CENTERED'
    const fontFamily = agency?.fontFamily ?? defaults.fontFamily
    const fontCss = resolveFontCss(fontFamily)
    const heroStyle = agency?.heroStyle ?? 'CLASSIC'
    const heroImageUrl = agency?.heroImageUrl ?? null
    const heroOverlayColor = agency?.heroOverlayColor ?? '#000000'
    const heroOverlayOpacity = agency?.heroOverlayOpacity ?? 0.3

    const t =
      variant === 'admin'
        ? correttoTheme
        : createTheme({
            palette: {
              primary: { main: primaryMain },
              secondary: { main: secondaryMain }
            },
            typography: {
              fontFamily: fontCss
            },
            components: {
              MuiButton: {
                styleOverrides: {
                  containedPrimary: {
                    backgroundColor: buttonMain,
                    '&:hover': {
                      backgroundColor: darken(buttonMain, 0.15)
                    }
                  }
                }
              }
            }
          })

    return {
      theme: t,
      buttonColorResolved: buttonMain,
      cardStyleResolved: cardStyle,
      headerStyleResolved: headerStyle,
      footerStyleResolved: footerStyle,
      fontFamilyResolved: fontFamily,
      fontCssResolved: fontCss,
      heroStyleResolved: heroStyle,
      heroImageUrlResolved: heroImageUrl,
      heroOverlayColorResolved: heroOverlayColor,
      heroOverlayOpacityResolved: heroOverlayOpacity
    }
  }, [agency, defaults, variant])

  // No backoffice o corpo fica na Manrope; a fonte da imobiliaria so e
  // carregada para as previas do site (ex.: descricao do imovel).
  const bodyFontCss = variant === 'admin' ? correttoFontCss : fontCssResolved

  useEffect(() => {
    loadGoogleFont(fontFamilyResolved)
    document.body.style.fontFamily = bodyFontCss
    return () => {
      document.body.style.fontFamily = ''
    }
  }, [fontFamilyResolved, bodyFontCss])

  useGtmInjector(variant === 'site' ? agency?.gtmContainerId : undefined)

  // O <title> do index.html e o da Corretto. No site e no portal a aba mostra a
  // imobiliaria (decisao D1), sem sobrescrever o titulo que a pagina definiu
  // (pagina personalizada, empreendimento)
  const agencyTitle = agency?.fantasyName || agency?.name
  useEffect(() => {
    if (variant === 'admin' || platformTitle || !agencyTitle) return
    if (document.title !== Constants.PLATFORM_TITLE) return
    document.title = agencyTitle
    return () => {
      if (document.title === agencyTitle) {
        document.title = Constants.PLATFORM_TITLE
      }
    }
  }, [variant, platformTitle, agencyTitle, pathname])

  // Favicon da imobiliaria com a mesma regra do titulo (decisao D1 do FAVICON)
  useAgencyFavicon(
    agency ? agency.faviconUrl ?? null : undefined,
    variant !== 'admin' && !platformTitle
  )

  const contextValue = useMemo(
    () => ({
      agency: agency ?? null,
      isLoading,
      buttonColor: buttonColorResolved,
      cardStyle: cardStyleResolved,
      headerStyle: headerStyleResolved,
      footerStyle: footerStyleResolved,
      fontFamily: fontFamilyResolved,
      fontCss: fontCssResolved,
      heroStyle: heroStyleResolved,
      heroImageUrl: heroImageUrlResolved,
      heroOverlayColor: heroOverlayColorResolved,
      heroOverlayOpacity: heroOverlayOpacityResolved
    }),
    [
      agency,
      isLoading,
      buttonColorResolved,
      cardStyleResolved,
      headerStyleResolved,
      footerStyleResolved,
      fontFamilyResolved,
      fontCssResolved,
      heroStyleResolved,
      heroImageUrlResolved,
      heroOverlayColorResolved,
      heroOverlayOpacityResolved
    ]
  )

  return (
    <AgencyThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </AgencyThemeContext.Provider>
  )
}

export function useAgencyTheme() {
  return useContext(AgencyThemeContext)
}
